import { api } from './api';

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const buf = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return buf;
}

export async function isPushSupported(): Promise<boolean> {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function getPushStatus(): Promise<'granted' | 'denied' | 'default' | 'unsupported'> {
  if (!(await isPushSupported())) return 'unsupported';
  return Notification.permission;
}

export async function enablePush(): Promise<{ ok: boolean; reason?: string }> {
  if (!(await isPushSupported())) return { ok: false, reason: 'Push is not supported on this device' };
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: 'Permission denied' };
  const r = await ensurePushSubscription();
  if (r.status === 'subscribed') return { ok: true };
  if (r.status === 'no-server-key') return { ok: false, reason: 'Push not configured on server' };
  return { ok: false, reason: r.error || 'Could not enable push' };
}

/** Ensures the browser has a PushSubscription AND that the server knows about
 *  it. Safe to call on every app load — the API uses `upsert` so re-sending
 *  the same endpoint is a no-op. Heals the "permission granted but no sub"
 *  state that happens after PWA reinstall, SW reset, or server-side cleanup
 *  of an expired subscription. */
export async function ensurePushSubscription(): Promise<{
  status: 'subscribed' | 'no-perm' | 'unsupported' | 'no-server-key' | 'failed';
  error?: string;
}> {
  if (!(await isPushSupported())) return { status: 'unsupported' };
  if (Notification.permission !== 'granted') return { status: 'no-perm' };
  try {
    const key = await api.pushKey();
    if (!key.enabled) return { status: 'no-server-key' };
    // One registration serves the whole origin: the PWA worker (with the push
    // handlers imported into it) in production, the standalone push worker in dev.
    let reg = await navigator.serviceWorker.getRegistration();
    if (!reg) reg = await navigator.serviceWorker.register(import.meta.env.DEV ? '/sw-push.js' : '/sw.js');
    await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key.publicKey),
      });
    }
    await api.pushSubscribe(sub.toJSON() as any);
    return { status: 'subscribed' };
  } catch (err: any) {
    return { status: 'failed', error: String(err?.message || err) };
  }
}

/** True if permission is granted AND a current PushSubscription exists in
 *  this browser. Used by the Settings UI so "enabled" reflects reality. */
export async function hasActiveSubscription(): Promise<boolean> {
  if (!(await isPushSupported())) return false;
  if (Notification.permission !== 'granted') return false;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return !!sub;
}

export async function disablePush(): Promise<void> {
  if (!(await isPushSupported())) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api.pushUnsubscribe(sub.endpoint).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
}
